import dayjs from 'dayjs';

import apiHandler from '@api/ApiHandler.ts';
import {
  Chat,
  ChatHistory,
  ChatRequest,
  CreateMessageRequest,
  CreateMessageResponse,
} from '@app/types/chat';
import { backendDateFormat } from '@utils/utils.ts';
import { TokenUsage } from '@api/openai/model/types';

/**
 * Send a chat message to the AI agent
 * @param data
 * @param callback
 * @returns The AI response as a message
 */
export const sendMessage = async (
  data: CreateMessageRequest,
  callback: (response: CreateMessageResponse) => void,
  errorCallback?: (error: any) => void
) => {
  apiHandler
    .post<CreateMessageResponse>(`/messages/`, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then((response) => {
      callback(response as any);
    })
    .catch((error) => {
      if (errorCallback) {
        errorCallback(error);
      } else {
        console.error('Error:', error);
        callback({} as any); // or handle the error as you see fit
      }
    });
};

/**
 * Get a list of Chats related to a user.
 * @param callback
 */
export const getChats = async (callback: (messages: Chat[]) => void) => {
  apiHandler
    .get<Chat[]>(`/chats/`)
    .then((response) => {
      callback(response as any);
    })
    .catch((error) => {
      console.error('Error:', error);
      callback(null as any); // or handle the error as you see fit
    });
};

/**
 * Get a list of Messages related to a chat.
 * @param id
 * @param callback
 */
export const getChatById = async (
  id: string,
  callback: (messages: Chat) => void
) => {
  apiHandler
    .get<Chat[]>(`/chats/${id}/`)
    .then((response) => {
      callback(response as any);
    })
    .catch((error) => {
      console.error('Error:', error);
      callback({} as any); // or handle the error as you see fit
    });
};

/**
 * Get chat history
 * @param chatType
 * @param callback
 */
export const getChatHistory = async (
  chatType: string,
  callback: (messages: ChatHistory) => void
) => {
  apiHandler
    .get<ChatHistory>(`/chats/history/?chat_type=${chatType}`)
    .then((response) => {
      callback(response as any);
    })
    .catch((error) => {
      console.error('Error:', error);
      callback(null as any); // or handle the error as you see fit
    });
};

/**
 * Create new chat
 * @param data
 * @param callback
 */
export const createChat = async (
  data: ChatRequest,
  callback: (messages: Chat) => void
) => {
  apiHandler
    .post<Chat>(`/chats/`, data)
    .then((response) => {
      callback(response as any);
    })
    .catch((error) => {
      console.error('Error:', error);
      callback({} as any); // or handle the error as you see fit
    });
};

/**
 * Edit a chat
 * @param id
 * @param data
 * @param callback
 */
export const editChat = async (
  id: string,
  data: ChatRequest,
  callback: (messages: Chat) => void
) => {
  apiHandler
    .put<Chat>(`/chats/${id}/`, data)
    .then((response) => {
      callback(response as any);
    })
    .catch((error) => {
      console.error('Error:', error);
      callback({} as any); // or handle the error as you see fit
    });
};

/**
 * delete a chat
 * @param id
 * @param callback
 */
export const deleteChat = async (id: number, callback: () => void) => {
  apiHandler
    .delete(`/chats/${id}/`)
    .then(() => {
      callback();
    })
    .catch((error) => {
      console.error('Error:', error);
      callback(); // or handle the error as you see fit
    });
};

export const getUserResults = async (callback: (response: any) => void) => {
  apiHandler
    .get(`/messages/user_files/`)
    .then((response) => {
      callback(response as any);
    })
    .catch((error) => {
      console.error('Error:', error);
      callback({} as any);
    });
};

export const getChatFile = async (
  id: string,
  callback: (response: any) => void,
  errorCallback: (response: any) => void
) => {
  apiHandler
    .get(`/chats/file/?file_id=${id}`)
    .then((response) => {
      callback(response as any);
    })
    .catch((error) => {
      errorCallback(error);
    });
};

export const getChatTokenUsageById = async (
  chatId: number,
  callback: (usage: TokenUsage) => void,
  errorCallback?: (error: any) => void
) => {
  apiHandler
    .get<TokenUsage>(`/chat-token-usage/?chat_id=${chatId}`)
    .then((response) => {
      callback(response as any);
    })
    .catch((error) => {
      console.error('Error:', error);
      if (errorCallback) {
        errorCallback(error);
      }
    });
};

export const getMonthlyChatTokenUsage = async (
  chatType: string,
  callback: (usage: TokenUsage) => void,
  errorCallback?: (error: any) => void
) => {
  const params = new URLSearchParams();
  const startOfMonth = dayjs().startOf('month').toDate();
  const endOfMonth = dayjs().endOf('month').toDate();
  params.append('created_at__date__gte', backendDateFormat(startOfMonth));
  params.append('created_at__date__lte', backendDateFormat(endOfMonth));
  params.append('chat_type', chatType);

  apiHandler
    .get<TokenUsage>(`/chat-token-usage/?${params.toString()}`)
    .then((response) => {
      callback(response as any);
    })
    .catch((error) => {
      console.error('Error:', error);
      if (errorCallback) {
        errorCallback(error);
      }
    });
};
